import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const TeamsApi = createApi({
  reducerPath: "Teams",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_BACKEND_URL}`,
    credentials: "include",
  }),
  endpoints: (builder) => ({
    getAllTeams: builder.query({
      query: (val) => {
        const page = val.page ?? 1;
        const limit = val.limit ?? 10;
        const sortBy = val.sortby ? `&sortBy=${val.sortby}` : "";
        const order = val.order ? `&order=${val.order}` : "";
        const search = val.search ? `&search=${val.search}` : "";
        const queryString = val.queryString
          ? `&${val.queryString.substring(1)}`
          : "";
        return `/companies/teams?page=${page}&limit=${limit}${search}${sortBy}${order}${queryString}`;
      },
    }),
    getSpecificTeam: builder.query({
      query: (id) => `/companies/teams/${id}`,
    }),
    deleteTeam: builder.mutation({
      query: (id) => ({
        url: `/teams/${id}`,
        method: "DELETE",
      }),
    }),
    deleteTeamAttachments: builder.mutation({
      query: (id) => ({
        url: `/companies/teams/attachments/${id}`,
        method: "DELETE",
      }),
    }),
    createTeam: builder.mutation({
      query: (payload) => {
        const formData = new FormData();

        // Append all fields to FormData
        Object.keys(payload).forEach((key) => {
          if (key === "attachments" && Array.isArray(payload[key])) {
            // Handle multiple files
            payload[key].forEach((file) => {
              formData.append("attachments", file);
            });
          } else if (key === "trades" && Array.isArray(payload[key])) {
            // Handle trades array
            payload[key].forEach((trade) => {
              formData.append("trades", trade);
            });
          } else if (key === "assignedZones" && Array.isArray(payload[key])) {
            // Handle assignedZones array
            payload[key].forEach((zone) => {
              formData.append("assignedZones", zone);
            });
          } else if (key === "memberUserIds" && Array.isArray(payload[key])) {
            // Handle memberUserIds array
            payload[key].forEach((userId) => {
              formData.append("memberUserIds", userId);
            });
          } else if (
            payload[key] !== null &&
            payload[key] !== undefined &&
            payload[key] !== ""
          ) {
            formData.append(key, payload[key]);
          }
        });

        return {
          url: "/companies/teams",
          method: "POST",
          body: formData,
        };
      },
    }),
    editTeam: builder.mutation({
      query: ({ id, payload }) => {
        const formData = new FormData();

        // Append all fields to FormData
        Object.keys(payload).forEach((key) => {
          if (key === "attachments" && Array.isArray(payload[key])) {
            // Handle multiple files
            payload[key].forEach((file) => {
              formData.append("attachments", file);
            });
          } else if (key === "trades" && Array.isArray(payload[key])) {
            // Handle trades array
            payload[key].forEach((trade) => {
              formData.append("trades", trade);
            });
          } else if (key === "assignedZones" && Array.isArray(payload[key])) {
            // Handle assignedZones array
            payload[key].forEach((zone) => {
              formData.append("assignedZones", zone);
            });
          } else if (key === "memberUserIds" && Array.isArray(payload[key])) {
            // Handle memberUserIds array
            payload[key].forEach((userId) => {
              formData.append("memberUserIds", userId);
            });
          } else if (
            payload[key] !== null &&
            payload[key] !== undefined &&
            payload[key] !== ""
          ) {
            formData.append(key, payload[key]);
          }
        });

        return {
          url: `/companies/teams/${id}`,
          method: "PUT",
          body: formData,
        };
      },
    }),
    deactivateTeam: builder.mutation({
      query: (id) => {
        const formData = new FormData();
        formData.append("status", "INACTIVE");

        return {
          url: `/companies/teams/${id}`,
          method: "PUT",
          body: formData,
        };
      },
    }),
    activateTeam: builder.mutation({
      query: (id) => {
        const formData = new FormData();
        formData.append("status", "ACTIVE");

        return {
          url: `/companies/teams/${id}`,
          method: "PUT",
          body: formData,
        };
      },
    }),
    createMember: builder.mutation({
      queryFn: async (payload, _queryApi, _extraOptions, baseQuery) => {
        const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
        const result = await fetch(`${baseUrl}/members`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(payload),
        });
        const data = await result.json();
        if (!result.ok) {
          return { error: { status: result.status, data } };
        }
        return { data };
      },
    }),
    deactivateMember: builder.mutation({
      query: (id) => ({
        url: `/members/${id}`,  
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: { disabled: true },
      }),
    }),
    activateMember: builder.mutation({
      query: (id) => ({
        url: `/members/${id}`,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: { disabled: false },
      }),
    }),
    editMember: builder.mutation({
      query: ({ id, val }) => ({
        url: `/members/${id}`,
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: val,
      }),
    }),
    getAllMembers: builder.query({
      query: (val) => {
        const page = val.page ?? 1;
        const limit = val.limit ?? 10000;
        const sortBy = val.sortby ? `&sortBy=${val.sortby}` : "";
        const sortOrder = val.sortorder ? `&sortOrder=${val.sortorder}` : "";
        const search = val.search ? `&search=${val.search}` : "";
        const queryString = val.queryString
          ? `&${val.queryString.substring(1)}`
          : "";
        return `/members?page=${page}&limit=${limit}${search}${sortBy}${sortOrder}${queryString}`;
      },
    }),
    getSpecificMember: builder.query({
      query: (id) => `/members/${id}`,
    }),
    assignMemberToTeam: builder.mutation({
      query: ({ teamId, memberId }) => ({
        url: `/members/teams/${teamId}/members`,
        method: "POST",
        body: { userId: memberId },
      }),
    }),
    unassignMemberFromTeam: builder.mutation({
      query: ({ teamId, memberId }) => ({
        url: `/members/teams/${teamId}/members/${memberId}`,
        method: "DELETE",
      }),
    }),
    getMemberCompanies: builder.query({
      query: (memberId) => `/members/${memberId}/companies`,
    }),
    assignMemberCompanies: builder.mutation({
      query: ({ memberId, payload }) => ({
        url: `/members/${memberId}/companies`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
      }),
    }),
    unassignMemberCompany: builder.mutation({
      query: ({ memberId, companyId }) => ({
        url: `/members/${memberId}/companies/${companyId}`,
        method: "DELETE",
      }),
    }),
  }),
});

export const {
  useGetAllTeamsQuery,
  useDeleteTeamMutation,
  useGetSpecificTeamQuery,
  useCreateTeamMutation,
  useEditTeamMutation,
  useDeleteTeamAttachmentsMutation,
  useDeactivateTeamMutation,
  useActivateTeamMutation,
  useCreateMemberMutation,
  useDeactivateMemberMutation,
  useActivateMemberMutation,
  useEditMemberMutation,
  useGetAllMembersQuery,
  useGetSpecificMemberQuery,
  useAssignMemberToTeamMutation,
  useUnassignMemberFromTeamMutation,
  useGetMemberCompaniesQuery,
  useAssignMemberCompaniesMutation,
  useUnassignMemberCompanyMutation,
} = TeamsApi;
